import React from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineClock, HiOutlineExclamationCircle } from 'react-icons/hi';
import { formatCalendarDateDMY } from '../../utils/timezoneDisplay';

function daysLabel(count, unit) {
  const n = Number(count) || 0;
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

function AlertList({ items, emptyText, accent, kind }) {
  const accentStyles = {
    amber: {
      header: 'bg-amber-50/80 border-amber-100',
      badge: items.length > 0 ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600',
      icon: 'text-amber-600',
      meta: 'text-amber-700',
      hover: 'hover:bg-amber-50/70',
    },
    rose: {
      header: 'bg-rose-50/80 border-rose-100',
      badge: items.length > 0 ? 'bg-rose-500 text-white' : 'bg-gray-200 text-gray-600',
      icon: 'text-rose-600',
      meta: 'text-rose-700',
      hover: 'hover:bg-rose-50/70',
    },
  };
  const style = accentStyles[accent] || accentStyles.amber;
  const Icon = kind === 'ended' ? HiOutlineExclamationCircle : HiOutlineClock;
  const title = kind === 'ended' ? 'Assessment ended' : 'Ending in 7 days';

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className={`border-b px-3 py-2.5 ${style.header}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Icon className={`h-5 w-5 shrink-0 ${style.icon}`} />
            <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          </div>
          <span
            className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-bold ${style.badge}`}
          >
            {items.length}
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col px-3 py-3 md:px-4">
        {items.length === 0 ? (
          <p className="flex flex-1 items-center justify-center py-6 text-center text-sm text-gray-500">
            {emptyText}
          </p>
        ) : (
          <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-100">
            {items.map((row) => (
              <li key={`${kind}-${row.plan_id}-${row.patient_id}`}>
                <Link
                  to={row.profile_path || `/patients/${row.patient_id}`}
                  className={`flex flex-col gap-0.5 px-3 py-2 ${style.hover} sm:flex-row sm:items-center sm:justify-between`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900">
                      {row.patient_name}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {row.patient_code}
                      {row.cycle_type === 'reassessment' ? ' · Re-assessment' : ' · Assessment'}
                    </span>
                  </span>
                  <span className="shrink-0 text-left sm:text-right">
                    <span className={`block text-xs font-medium ${style.meta}`}>
                      {kind === 'ended'
                        ? `Ended ${daysLabel(row.days_overdue, 'day')} ago`
                        : row.days_left === 0
                          ? 'Ends today'
                          : `${daysLabel(row.days_left, 'day')} left`}
                    </span>
                    <span className="block text-[11px] text-gray-500">
                      Due {formatCalendarDateDMY(row.due_date)}
                      {row.target_sessions ? ` · ${row.target_sessions} sessions` : ''}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const SessionEndingAlertsCard = ({ endingSoon, ended, loading }) => {
  const soon = Array.isArray(endingSoon) ? endingSoon : [];
  const overdue = Array.isArray(ended) ? ended : [];
  const total = soon.length + overdue.length;

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-gray-100 bg-amber-50/60 px-3 py-2 md:px-4 md:py-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 md:h-10 md:w-10">
          <HiOutlineClock className="h-5 w-5 text-amber-600" />
        </div>
        <div className="min-w-0">
          <h3 className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-gray-900 md:text-base">
            <span>Assessment ending alerts</span>
            {total > 0 && (
              <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white">
                {total}
              </span>
            )}
          </h3>
          <p className="truncate text-[11px] text-gray-500 md:text-xs">
            Leaves the list after a new re-assessment is created
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 p-3 md:grid-cols-2 md:gap-4 md:p-4">
          <AlertList
            kind="soon"
            accent="amber"
            items={soon}
            emptyText="No assessments ending in the next 7 days"
          />
          <AlertList
            kind="ended"
            accent="rose"
            items={overdue}
            emptyText="No ended assessments"
          />
        </div>
      )}
    </section>
  );
};

export default SessionEndingAlertsCard;
